<script>
import buttonOutline from "@/components/buttons/buttonOutline.vue";
import { Icon } from '@iconify/vue';
import zetrix from "../../utils/zetrix.js";
import { shortenUserName } from "../../utils/filter";
import { toZtx } from "../../utils/util.js";

export default {
  name: "MenuBar",
  components: {
    buttonOutline,
    Icon,
  },
  mixins: [zetrix],
  inject: ["sharedService"],
  data() {
    return {
      select: "Select Account",
      snackbar: false,
      copyText: "Address copied",
      timeout: 2000,
      jointAddress: sessionStorage.getItem("jointAddress"),
      jointBalance: "0.0",
      buttonName: "New Transaction",
      iconPlacement: "prepend",
      buttonIcon: "mdi-plus",
      notificationText: "new notifications",
      jointAccountList: [],
      notificationList: [],
      openGroups: {}, // track list-group open/close state
       role: sessionStorage.getItem("userRole") || "", // fallback only
      menuList: [
        {
          text: "Certificates",
          icon: "mdi-file-document-outline",
          route: "/certificate",
          roles: ["ISSUER", "MAKER"],
        },
        {
          text: "Templates",
          icon: "mdi:users-outline",
          roles: ["ISSUER", "MAKER"],
          children: [
            {
              text: "Template List",
              icon: "material-symbols:mobile-layout-outline",
              route: "/template",
              roles: ["ISSUER", "MAKER"],
            },
            {
              text: "VC Design",
              icon: "lucide:edit",
              route: "/vc-design",
              roles: ["ISSUER", "MAKER"],
            },
          ],
        },
        {
          text: "Settings",
          icon: "mdi-cog-outline",
          roles: ["ISSUER", "MAKER"], // only admin
          children: [
            {
              text: "User Management",
              icon: "mdi-account-cog-outline",
              route: "/settings",
              roles: ["ISSUER", "MAKER"],
            },
            {
              text: "Audit Log",
              icon: "mdi-file-cog-outline",
              route: "/audit-log",
              roles: ["ISSUER", "MAKER"],
            },
          ],
        },
        {
          text: "Issuer Application",
          icon: "mdi-file-document-outline",
          route: "/issuer/application-list",
          roles: ["ADMIN"], // only admin
        },
        {
          text: "Vc Design Management",
          icon: "mdi-file-document-outline",
          route: "/issuer/vc-design-review-list",
          roles: ["ADMIN"], // only admin
        },
      ]

    };
  },
 computed: {
  filteredMenu() {
    return this.menuList
      .map(item => {
        // filter children if exist
        if (item.children) {
          const filteredChildren = item.children.filter(child =>
            child.roles.includes(this.role)
          );
          return filteredChildren.length
            ? { ...item, children: filteredChildren }
            : null;
        }
        return item.roles.includes(this.role) ? item : null;
      })
      .filter(Boolean);
  }
},

  mounted() {
    // set initial open state for groups containing current route
    this.menuList.forEach((item, index) => {
      this.openGroups[index] = !!(
        item.children && this.isCurrentRoute(item.route, item.children)
      );
    });

  },
  methods: {

    convertToZtx(balance) {
      return toZtx(balance);
    },

    copyAddress(address) {
      const textField = document.createElement("textarea");
      textField.innerText = address;
      document.body.appendChild(textField);
      textField.select();
      document.execCommand("copy");
      textField.remove();
      this.snackbar = true;
    },
    shortenUserName(string) {
      return shortenUserName(string);
    },
    isCurrentRoute(path, children = []) {
      const isActive = this.$route.path === path ||
        (children.length > 0 && children.some((child) => this.$route.path === child.route));
      // Uncomment for debugging: console.log('Route check:', path, this.$route.path, isActive);
      return isActive;
    },
    navigateTo(path) {
      if (this.$route.path === path) {
        this.$router.go();
      } else {
        this.$router.push(path);
      }
    },
  },
};
</script>

<template>
  <div class="menu-bar">
    <!-- Image on top -->
    <v-img :width="isSmallScreen ? '128' : '150'" aspect-ratio="16/9" cover
      src="../../assets/images/Zetrix-Logo-small-black.png" class="responsive-img ml-4 my-4"
      @click="checkJointIsExist"></v-img>

    <!-- The menu list using navigation drawer structure -->
    <div class="nav-items">
      <template v-for="(item, i) in filteredMenu" :key="i">
        <!-- Parent with children -->
        <div v-if="item.children" class="nav-group" :class="{ active: isCurrentRoute(item.route, item.children) }">
          <div class="nav-link" @click="openGroups[i] = !openGroups[i]">
            <div class="nav-link-content">
              <Icon :icon="item.icon" class="nav-item-icon" />
              <span class="nav-item-title">{{ item.text }}</span>
            </div>
          </div>

          <!-- Children -->
          <div v-show="openGroups[i]" class="nav-group-children">
            <div v-for="(child, j) in item.children" :key="j" class="nav-link nav-link-child"
              :class="{ 'router-link-exact-active': isCurrentRoute(child.route) }" @click="navigateTo(child.route)">
              <div class="nav-link-content">
                <Icon :icon="child.icon" class="nav-item-icon" />
                <span class="nav-item-title">{{ child.text }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Parent without children -->
        <div v-else class="nav-link" :class="{ 'router-link-exact-active': isCurrentRoute(item.route) }"
          @click="navigateTo(item.route)">
          <div class="nav-link-content">
            <Icon :icon="item.icon" class="nav-item-icon" />
            <span class="nav-item-title">{{ item.text }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="timeout">
      {{ copyText }}
      <template v-slot:actions>
        <v-btn color="blue" variant="text" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>


<style>
@import "@/assets/styles/vertical_nav.css";

.menu-bar {
  padding-top: 18px;
  padding-left: 12px;
}


/* Navigation drawer structure styles */
.nav-items {
  padding: 8px 12px;
}

.nav-link {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 3.625rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.25rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #92939E;
  font-size: 16px;
  font-family: "Gilroy", sans-serif;
  font-weight: 600;
}

.nav-link-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.nav-item-icon {
  margin-right: 0.75rem;
  color: #92939E;
  width: 24px;
  height: 24px;
}

.nav-item-title {
  font-weight: bold;
  flex: 1;
}

.nav-group-arrow {
  margin-left: auto;
  color: #000000de;
  width: 24px;
  height: 24px;
}

.nav-link:hover {
  background-color: rgba(220, 64, 77, 0.05);
}

/* Child navigation styles */
.nav-group-children {
  margin-left: 1rem;
  border-left: 2px solid rgba(220, 64, 77, 0.1);
  padding-left: 0.5rem;
}

.nav-link-child {
  min-height: 3rem;
  padding: 0.5rem 1rem;
  margin-left: 1rem;
}

/* Active status indicator */
.nav-link.router-link-exact-active,
.nav-group.active:not(.nav-group .nav-group)>.nav-link:first-child {
  color: #f33b4b;
  background-color: rgba(220, 64, 77, 0.1);
}

/* Active icon color for all active menu items */
.nav-link.router-link-exact-active .nav-item-icon,
.nav-group.active:not(.nav-group .nav-group)>.nav-link:first-child .nav-item-icon,
.nav-link-child.router-link-exact-active .nav-item-icon {
  color: #C22328;
}

/* Specific positioning for parent menu indicators - higher specificity */
.nav-group.active:not(.nav-group .nav-group)>.nav-link:first-child::after {
  position: absolute;
  background-color: #dc404d;
  height: 100%;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
  content: "";
  width: 0.25rem;
  right: -0.8rem !important;
  top: 0;
}

/* Active indicator for direct nav-links (not children and not parent menus) */
.nav-link.router-link-exact-active:not(.nav-link-child):not(.nav-group>.nav-link:first-child)::after {
  position: absolute;
  background-color: #dc404d;
  height: 100%;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
  content: "";
  width: 0.25rem;
  right: -0.8rem;
  top: 0;
}

/* Child items only change text color, no indicator */
.nav-link-child.router-link-exact-active {
  color: #f33b4b;
  background-color: transparent;
}

/* Ensure the navigation container allows indicators to show */
.nav-items {
  position: relative;
  overflow: visible;
}

.nav-link {
  position: relative;
  overflow: visible;
}

.nav-group {
  overflow: visible;
}

/* .custom-btn .v-btn__content {
  font-size: 16px;
  color: #f33b4b;
} */
</style>
